import React, { useState, useEffect, useRef } from 'react';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';
import Header from '../components/Header';
import InventoryPage from './InventoryPage';
import PetsCheckinPage from './PetsCheckinPage';
import RehomingPage from './RehomingPage';
import CameraView from '../components/CameraView';
import ConferencePage from './ConferencePage';
import ConferenceBanner from '../components/ConferenceBanner';
import ConferenceCall from '../components/ConferenceCall';
import { bindConferenceSocket, loadConferenceState } from '../services/conference';
import ArrivalModal from './ArrivalModal';
import socketService from '../services/socket';
import { getMessages, getMessagesWithReadStatus, getOlderMessages, getUnreadCounts, getAllMessages, getOlderAllMessages } from '../services/api';
import PushNotifications from '../services/pushNotifications';
import SERVER_CONFIG from '../services/config';
import '../styles/ChatPage.css';

const INVENTORY_CHANNEL = 'kormabd';
const ALL_CHANNEL_ID = 'all';
const PETS_CHANNEL = 'pets';
const REHOMING_CHANNEL = 'rehoming';
const ARRIVAL_CHANNEL = 'package';
const WB_CHANNEL = 'wb';
const VETCAM_CHANNEL = 'vetcam';
const CONFERENCE_CHANNEL = 'conference';

function ChatPage({ user, channels, onLogout }) {
  // Определяем начальный канал
  const getInitialChannel = () => {
    // Проверить URL параметр channel (из push-уведомления)
    const urlParams = new URLSearchParams(window.location.search);
    const channelIdFromUrl = urlParams.get('channel');

    if (channelIdFromUrl) {
      const channelFromUrl = channels.find(ch => ch.id === parseInt(channelIdFromUrl));
      if (channelFromUrl) {
        // Очистить URL параметр после использования
        window.history.replaceState({}, '', window.location.pathname);
        return channelFromUrl;
      }
    }

    // Иначе использовать дефолтный канал
    if (user.defaultChannelId) {
      const defaultChannel = channels.find(ch => ch.id === user.defaultChannelId);
      if (defaultChannel) return defaultChannel;
    }
    return channels[0];
  };

  const [selectedChannel, setSelectedChannel] = useState(getInitialChannel());
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [showHiddenCodes, setShowHiddenCodes] = useState(false);
  const [hiddenCodes, setHiddenCodes] = useState([]);
  const [hiddenCodesLoading, setHiddenCodesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Map()); // userId → displayName
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState([]);
  const [pushNotifications, setPushNotifications] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const wasDisconnectedRef = useRef(false);
  const pendingScrollRef = useRef(null);

  const selectedChannelRef = useRef(selectedChannel);
  const userRef = useRef(user);

  useEffect(() => {
    selectedChannelRef.current = selectedChannel;
  }, [selectedChannel]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Подстройка высоты под реальный видимый viewport (Android nav bar)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Инициализация push-уведомлений
  useEffect(() => {
    const initPush = async () => {
      const push = new PushNotifications(SERVER_CONFIG.apiUrl);
      setPushNotifications(push);

      if (push.isSupported()) {
        const permission = push.getPermission();
        if (permission === 'default') {
          // Спросить пользователя о разрешении
          const granted = await push.requestPermission();
          if (granted) {
            const token = localStorage.getItem('token');
            try {
              await push.subscribe(token);
              console.log('[Push] Подписка успешна');
            } catch (err) {
              console.error('[Push] Ошибка подписки:', err);
            }
          }
        } else if (permission === 'granted') {
          // Всегда перерегистрируем — удаляет старые подписки от других аккаунтов на этом устройстве
          const token = localStorage.getItem('token');
          try {
            await push.subscribe(token);
            console.log('[Push] Подписка успешна');
          } catch (err) {
            console.error('[Push] Ошибка подписки:', err);
          }
        }
      }
    };

    initPush();
  }, []);

  // Обработка сообщений от Service Worker (навигация из push-уведомлений)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE_TO_CHANNEL') {
        const channelId = event.data.channelId;
        console.log('[ChatPage] Navigating to channel from SW:', channelId);

        // Найти канал по ID и переключиться на него
        const channel = channels.find(ch => ch.id === parseInt(channelId));
        if (channel) {
          setSelectedChannel(channel);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, [channels]);

  // Подключение к WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    bindConferenceSocket();
    socketService.connect(token);
    loadConferenceState();

    socketService.on('message:new', handleNewMessage);
    socketService.on('message:edited', handleMessageEdited);
    socketService.on('message:deleted', handleMessageDeleted);
    socketService.on('message:read', handleMessageRead);
    socketService.on('typing:start', handleTypingStart);
    socketService.on('typing:stop', handleTypingStop);

    socketService.onDisconnect(() => {
      wasDisconnectedRef.current = true;
      setIsConnected(false);
    });

    socketService.onConnect(() => {
      if (!wasDisconnectedRef.current) return;
      wasDisconnectedRef.current = false;
      setIsConnected(true);
      // Подгрузить пропущенные сообщения для текущего канала
      const ch = selectedChannelRef.current;
      if (ch) {
        getMessagesWithReadStatus(ch.id).then(data => {
          setMessages(data.messages || []);
          setFirstUnreadMessageId(data.firstUnreadMessageId || null);
        }).catch(() => {});
      }
      loadUnreadCounts();
    });

    return () => {
      socketService.off('message:new');
      socketService.off('message:edited');
      socketService.off('message:deleted');
      socketService.off('message:read');
      socketService.off('typing:start');
      socketService.off('typing:stop');
      socketService.onDisconnect(null);
      socketService.onConnect(null);
      socketService.disconnect();
    };
  }, []);

  // Форсировать реконнект при пробуждении экрана
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        socketService.forceReconnect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Загрузка счетчиков непрочитанных
  useEffect(() => {
    loadUnreadCounts();
  }, []);

  // Загрузка сообщений при смене канала
  useEffect(() => {
    console.log('[ChatPage] selectedChannel changed to:', selectedChannel);
    if (selectedChannel) {
      loadMessages();
    }
  }, [selectedChannel]);

  const loadMessages = async () => {
    const channelId = selectedChannel.id;
    setIsLoadingMessages(true);
    try {
      if (channelId === ALL_CHANNEL_ID) {
        const msgs = await getAllMessages(50);
        setMessages(msgs || []);
        setFirstUnreadMessageId(null);
      } else {
        console.log('[ChatPage] Loading messages for channel:', channelId);
        const data = await getMessagesWithReadStatus(channelId);
        const msgs = data.messages || [];
        setMessages(msgs);
        setFirstUnreadMessageId(data.firstUnreadMessageId || null);
        if (msgs.length > 0) {
          const lastId = msgs[msgs.length - 1].id;
          socketService.markAsRead(channelId, lastId);
        }
        if (pendingScrollRef.current) {
          setFirstUnreadMessageId(pendingScrollRef.current);
          pendingScrollRef.current = null;
        } else {
          setFirstUnreadMessageId(null);
        }
        setUnreadCounts(prev =>
          prev.map(c => c.channelId === channelId ? { ...c, unreadCount: 0 } : c)
        );
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      try {
        if (selectedChannel.id !== ALL_CHANNEL_ID) {
          const messages = await getMessages(channelId);
          setMessages(messages || []);
          setFirstUnreadMessageId(null);
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      console.log('[ChatPage] Loading unread counts...');
      const counts = await getUnreadCounts();
      console.log('[ChatPage] Received unread counts:', counts);
      setUnreadCounts(counts);
    } catch (error) {
      console.error('[ChatPage] Failed to load unread counts:', error);
    }
  };

  const handleLoadOlderMessages = async (beforeMessageId) => {
    try {
      let olderMessages;
      if (selectedChannel.id === ALL_CHANNEL_ID) {
        olderMessages = await getOlderAllMessages(beforeMessageId, 50);
      } else {
        console.log('[ChatPage] Loading older messages before:', beforeMessageId);
        olderMessages = await getOlderMessages(selectedChannel.id, beforeMessageId, 20);
      }
      if (olderMessages && olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to load older messages:', error);
      return false;
    }
  };

  const handleMarkAsRead = (lastReadMessageId) => {
    // Отправить через WebSocket
    socketService.markAsRead(selectedChannel.id, lastReadMessageId);

    // Обновить локальное состояние
    setFirstUnreadMessageId(null);

    // Обновить счетчики
    setUnreadCounts(prev =>
      prev.map(c =>
        c.channelId === selectedChannel.id
          ? { ...c, unreadCount: 0 }
          : c
      )
    );
  };

  const ALL_MODE_EXCLUDED_TYPES = ['attendance', 'ban_notification', 'supply_arrival', 'marketplace_code', 'pets_checkin'];

  const handleNewMessage = (message) => {
    const currentChannel = selectedChannelRef.current;
    if (currentChannel?.id === ALL_CHANNEL_ID) {
      // Фильтруем системные и бот-сообщения
      if (ALL_MODE_EXCLUDED_TYPES.includes(message.messageType) || message.username?.endsWith('_bot')) return;
      // В режиме "Все" — обогащаем сообщение данными канала и добавляем
      const channelInfo = channels.find(ch => ch.id === message.channelId);
      setMessages(prev => [...prev, {
        ...message,
        channelName: channelInfo?.name,
        channelDisplayName: channelInfo?.display_name,
        channelIcon: channelInfo?.icon,
      }]);
    } else if (message.channelId === currentChannel?.id) {
      console.log('[ChatPage] Adding new message to current channel:', message);
      setMessages(prev => [...prev, message]);
    } else {
      console.log('[ChatPage] New message in different channel:', message.channelId);
      setUnreadCounts(prev =>
        prev.map(c => c.channelId === message.channelId
          ? { ...c, unreadCount: (c.unreadCount || 0) + 1 }
          : c
        )
      );
    }
  };

  const handleMessageEdited = ({ messageId, content }) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, content, editedAt: new Date() } : msg
    ));
  };

  const handleMessageDeleted = ({ messageId }) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  const handleMessageRead = ({ channelId, lastReadMessageId }) => {
    // Синхронизация статуса прочитанности между устройствами
    if (channelId === selectedChannel.id) {
      setFirstUnreadMessageId(null);
    }

    // Обновить счетчики
    loadUnreadCounts();
  };

  const handleTypingStart = ({ channelId, userId, displayName }) => {
    const currentChannel = selectedChannelRef.current;
    const currentUser = userRef.current;
    if (channelId === currentChannel?.id && userId !== currentUser.id) {
      setTypingUsers(prev => new Map([...prev, [userId, displayName]]));
    }
  };

  const handleTypingStop = ({ channelId, userId }) => {
    setTypingUsers(prev => {
      const newMap = new Map(prev);
      newMap.delete(userId);
      return newMap;
    });
  };

  const handleSendMessage = (content, messageType, replyToMessageId) => {
    socketService.sendMessage(selectedChannel.id, content, messageType, replyToMessageId);
    setReplyToMessage(null);
  };

  const handleStartTyping = () => {
    socketService.startTyping(selectedChannel.id);
  };

  const handleStopTyping = () => {
    socketService.stopTyping(selectedChannel.id);
  };


  const handleArrivalOpen = () => {
    setArrivalOpen(true);
  };

  const handleReply = (message) => {
    setReplyToMessage(message);
  };

  const handleCancelReply = () => {
    setReplyToMessage(null);
  };

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    setFirstUnreadMessageId(null);
  };

  const handleNavigateToChannel = (channelId, messageId) => {
    const channel = channels.find(ch => ch.id === channelId);
    if (channel) {
      if (messageId) pendingScrollRef.current = messageId;
      setSelectedChannel(channel);
    }
  };

  const ADMIN_API = 'https://zhylytaban.kz/admin/api';
  const SYSTEM_KEY = 'shelter_chat_sync_2026';
  const MP_ICONS = { ozon: '/mp-icons/ozon.webp', wb: '/mp-icons/wb.png', ym: '/mp-icons/ym.png' };

  const loadHiddenCodes = async () => {
    setHiddenCodesLoading(true);
    try {
      const res = await fetch(`${SERVER_CONFIG.apiUrl}/marketplace/history`);
      const data = await res.json();
      setHiddenCodes(Array.isArray(data) ? data : []);
    } catch(e) { setHiddenCodes([]); console.error('[HiddenCodes] error:', e); }
    setHiddenCodesLoading(false);
  };

  const restoreCode = async (id) => {
    await fetch(`${SERVER_CONFIG.apiUrl}/marketplace/restore/${id}`, {
      method: 'POST',
      headers: { 'X-System-Key': SYSTEM_KEY },
    });
    setHiddenCodes(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="chat-page">
      <Header
        user={user}
        onLogout={onLogout}
        channels={channels}
        selectedChannel={selectedChannel}
        onSelectChannel={handleChannelSelect}
        unreadCounts={unreadCounts}
        isConnected={isConnected}
      />

      <ConferenceBanner user={user} />

      <div className="chat-container">
        <div className="chat-main">
          {selectedChannel?.name?.trim() === REHOMING_CHANNEL ? (
            <RehomingPage user={user} channel={selectedChannel} />
          ) : selectedChannel?.name === PETS_CHANNEL ? (
            <PetsCheckinPage user={user} />
          ) : selectedChannel?.name === INVENTORY_CHANNEL ? (
            <InventoryPage user={user} />
          ) : selectedChannel?.name?.trim() === VETCAM_CHANNEL ? (
            <CameraView />
          ) : selectedChannel?.name?.trim() === CONFERENCE_CHANNEL ? (
            <ConferencePage user={user} />
          ) : (
            <>
              <div className="channel-header">
                {selectedChannel?.description && (
                  <p className="channel-description">{selectedChannel.description}</p>
                )}
                {selectedChannel?.name?.trim() === ARRIVAL_CHANNEL && (
                  <button className="arrival-scan-btn" onClick={handleArrivalOpen} title="Записать приход">
                    +
                  </button>
                )}
                {selectedChannel?.name?.trim() === WB_CHANNEL && (
                  <button className="arrival-scan-btn" style={{ background: '#9c27b0', fontSize: 13, padding: '6px 12px', borderRadius: 8, width: 'auto' }}
                    onClick={() => { setShowHiddenCodes(true); loadHiddenCodes(); }}
                    title="Скрытые коды">
                    📦 Скрытые
                  </button>
                )}
              </div>

              {showHiddenCodes && selectedChannel?.name?.trim() === WB_CHANNEL && (
                <div style={{
                  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                }} onClick={() => setShowHiddenCodes(false)}>
                  <div style={{
                    background: '#fff', borderRadius: '16px 16px 0 0', padding: 16,
                    width: '100%', maxWidth: 480, maxHeight: '70vh', overflowY: 'auto',
                  }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <b style={{ fontSize: 15 }}>📦 История кодов</b>
                      <button onClick={() => setShowHiddenCodes(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}>✕</button>
                    </div>
                    {hiddenCodesLoading && <div style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>Загрузка...</div>}
                    {!hiddenCodesLoading && hiddenCodes.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>Нет скрытых кодов</div>
                    )}
                    {hiddenCodes.map(item => (
                      <div key={item.id} style={{
                        border: '1px solid #eee', borderRadius: 8, padding: '10px 12px', marginBottom: 8,
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        {MP_ICONS[item.marketplace] && (
                          <img src={MP_ICONS[item.marketplace]} alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 18, letterSpacing: 2 }}>
                            {item.code || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: '#aaa' }}>{item.sender_name}</div>
                        </div>
                        <button onClick={() => restoreCode(item.id)} style={{
                          background: '#ff9800', color: '#fff', border: 'none',
                          borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                        }}>↩ Вернуть</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <MessageList
                onReply={selectedChannel?.id === ALL_CHANNEL_ID ? null : handleReply}
                messages={messages}
                currentUser={user}
                isLoading={isLoadingMessages}
                typingUsers={new Set(typingUsers.values())}
                firstUnreadMessageId={firstUnreadMessageId}
                onLoadOlderMessages={handleLoadOlderMessages}
                onMarkAsRead={handleMarkAsRead}
                onDelete={(id) => handleMessageDeleted({ messageId: id })}
                isAllMode={selectedChannel?.id === ALL_CHANNEL_ID}
                onNavigateToChannel={handleNavigateToChannel}
              />

              {selectedChannel?.id !== ALL_CHANNEL_ID && (
                <MessageInput
                  replyToMessage={replyToMessage}
                  onCancelReply={handleCancelReply}
                  channelId={selectedChannel?.id}
                  onSendMessage={handleSendMessage}
                  onStartTyping={handleStartTyping}
                  onStopTyping={handleStopTyping}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ArrivalModal
        isOpen={arrivalOpen}
        onClose={() => setArrivalOpen(false)}
      />

      <ConferenceCall user={user} />
    </div>
  );
}

export default ChatPage;
